import React, { useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Search, X } from 'lucide-react';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { ProductTile } from './ProductTile';
import { getCollection } from '@/data/catalog';
import { useProducts } from '@/context/ProductsContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn } from '@/lib/utils';

// Studio filter buckets — mapped against `originalCategory`.
// "prints" is a super-filter that includes both Limited Editions and Open Editions.
const STUDIO_FILTERS = [
  { key: 'prints', label: 'Prints', match: (p) => p.originalCategory === 'limited-edition-prints' || p.originalCategory === 'prints' },
  { key: 'limited-edition-prints', label: 'Limited Editions', match: (p) => p.originalCategory === 'limited-edition-prints' },
  { key: 'open-editions', label: 'Open Editions', match: (p) => p.originalCategory === 'prints' },
  { key: 'paper-goods', label: 'Paper Goods', match: (p) => p.originalCategory === 'paper-goods' },
  { key: 'all', label: 'All' },
];

export const CollectionPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const collection = getCollection(slug);
  const { getProductsByCollection } = useProducts();
  const [query, setQuery] = useState('');
  // Default Studio view = Prints (both Limited + Open editions).
  const [filter, setFilter] = useState(slug === 'studio' ? 'prints' : 'all');
  // Originals-only: hide pieces already in private collections.
  const [availableOnly, setAvailableOnly] = useState(false);

  usePageMeta({
    title: collection
      ? `${collection.name} — Graves Collective`
      : 'Collection — Graves Collective',
    description: collection?.blurb,
    image: collection?.feature_image,
  });

  const allProducts = getProductsByCollection(slug);
  const isStudio = slug === 'studio';
  const isOriginals = slug === 'originals';

  const products = useMemo(() => {
    if (isOriginals) {
      return availableOnly ? allProducts.filter((p) => !p.sold) : allProducts;
    }
    if (!isStudio) return allProducts;
    const q = query.trim().toLowerCase();
    const activeFilter = STUDIO_FILTERS.find((f) => f.key === filter);
    return allProducts.filter((p) => {
      if (activeFilter && activeFilter.match && !activeFilter.match(p)) return false;
      if (q && !`${p.title} ${p.description || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allProducts, isStudio, isOriginals, availableOnly, query, filter]);

  const soldCount = isOriginals ? allProducts.filter((p) => p.sold).length : 0;

  if (!collection) {
    return <Navigate to="/404" replace />;
  }

  const isApparel = slug === 'apparel';
  const heroImage = isApparel
    ? 'https://customer-assets.emergentagent.com/job_9cdf2342-a607-473f-8553-b1e4a60c73ce/artifacts/gxbo3noe_back%20of%20hoodie.png'
    : null;

  return (
    <div
      className="min-h-screen bg-cream text-ink"
      data-testid={`page-collection-${slug}`}
    >
      <SiteHeader />

      {/* Apparel — lifestyle hero band */}
      {heroImage && (
        <section
          className="relative w-full bg-ink overflow-hidden"
          data-testid="apparel-hero"
        >
          <div className="relative h-[55vh] min-h-[420px]">
            <img
              src={heroImage}
              alt="Worn in the wild"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/20 to-ink/30" />
          </div>
        </section>
      )}

      {/* Editorial collection header */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-20 pb-16 lg:pb-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-soft hover:text-terracotta transition-colors mb-10 group"
          data-testid="breadcrumb-home"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1" />
          Home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">
              Collection
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-ink leading-[0.95] tracking-tight">
              {collection.name}
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-soft max-w-2xl leading-relaxed">
              {collection.blurb}
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <p className="text-sm text-soft">
              {products.length} {products.length === 1 ? 'piece' : 'pieces'}{' '}
              {isStudio && (query || filter !== 'prints')
                ? 'matching'
                : isOriginals && availableOnly
                ? 'available now'
                : 'available'}
            </p>
          </div>
        </div>

        {/* Originals — available-only toggle */}
        {isOriginals && soldCount > 0 && (
          <div
            className="mt-12 flex items-center justify-between gap-6 pt-8 border-t border-ink/10"
            data-testid="originals-availability"
          >
            <p className="text-sm text-soft">
              <span className="tabular-nums text-ink font-medium">{soldCount}</span>
              {' '}of these have found their homes.
            </p>
            <label className="inline-flex items-center gap-3 cursor-pointer text-xs uppercase tracking-[0.18em] text-ink">
              <input
                type="checkbox"
                checked={availableOnly}
                onChange={(e) => setAvailableOnly(e.target.checked)}
                className="w-4 h-4 accent-ink"
                data-testid="originals-available-toggle"
              />
              Available only
            </label>
          </div>
        )}

        {/* Studio-only search + filter bar */}
        {isStudio && (
          <div
            className="mt-12 flex flex-col lg:flex-row lg:items-center gap-6 pt-8 border-t border-ink/10"
            data-testid="studio-filters"
          >
            <div className="relative w-full lg:max-w-sm">
              <Search className="w-4 h-4 text-soft absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search prints, cards, editions…"
                className="w-full pl-10 pr-9 py-3 text-sm bg-transparent border border-ink/20 rounded-none focus:outline-none focus:border-ink placeholder:text-soft/70 text-ink"
                data-testid="studio-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-soft hover:text-ink transition-colors"
                  aria-label="Clear search"
                  data-testid="studio-search-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {STUDIO_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    'px-4 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors',
                    filter === f.key
                      ? 'bg-ink text-cream border-ink'
                      : 'bg-transparent text-soft border-ink/20 hover:text-ink hover:border-ink'
                  )}
                  data-testid={`studio-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-32">
        {products.length === 0 ? (
          <div className="text-center py-24" data-testid="studio-empty-state">
            <p className="text-soft">
              {isStudio && (query || filter !== 'prints')
                ? 'Nothing matches those filters yet.'
                : 'Nothing in this collection yet — check back soon.'}
            </p>
            {isStudio && (query || filter !== 'prints') && (
              <button
                onClick={() => {
                  setQuery('');
                  setFilter('prints');
                }}
                className="mt-4 text-xs uppercase tracking-[0.18em] text-terracotta hover:underline"
                data-testid="studio-reset-filters"
              >
                Reset filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {products.map((p, idx) => (
              <ProductTile key={p.id} product={p} eager={idx < 3} />
            ))}
          </div>
        )}
      </section>

      <SiteFooter />
    </div>
  );
};

export default CollectionPage;
